import { reactive, ref } from 'vue'

// Shared quote-journey validation state, surfaced at the top of the current step (Option A) and, for
// server-flagged fields, inline via the `no_errors` vueform rule (Option B). Module-level singletons so
// the banner, the rule and the controllers all read the same state.
const summary = ref<string[]>([])
// Increments on every failed validation attempt (client or server), even when the error set is identical,
// so the step can reliably re-scroll to the first invalid field each submit (summary content may not change).
const attemptId = ref(0)
export const serverFieldErrors = reactive<Record<string, string>>({})

const GENERIC = 'There was a problem getting your quote. Please try again, or call us on 0800 640 6600.'

const clearFields = () => { for (const k of Object.keys(serverFieldErrors)) delete serverFieldErrors[k] }

export function useQuoteErrors() {
    // Top-of-page summary from client-side validation (list of messages).
    const setClient = (messages: string[]) => {
        summary.value = [...new Set(messages.filter(Boolean))]
        attemptId.value++
    }

    // Server response: 4xx surfaces the validation errors / message; 5xx (or unknown) is always the
    // standard message — server internals (exceptions, dev error pages) must never reach the UI.
    const setServer = (body: unknown, status = 500) => {
        clearFields()
        attemptId.value++
        if (status >= 500) { summary.value = [GENERIC]; return }
        const messages: string[] = []
        if (body && typeof body === 'object') {
            // ValidationProblemDetails carries the field dict under `errors`; a raw ModelState is the dict itself.
            const dict = ((body as any).errors ?? body) as Record<string, unknown>
            for (const key of Object.keys(dict)) {
                const val = dict[key]
                const msg = Array.isArray(val) ? String(val[0]) : String(val)
                if (!msg) continue
                if (key) serverFieldErrors[key] = msg
                messages.push(msg)
            }
        } else if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) {
            messages.push(body.trim())
        }
        summary.value = messages.length ? [...new Set(messages)] : [GENERIC]
    }

    const clear = () => { summary.value = []; clearFields() }

    return { summary, attemptId, setClient, setServer, clear }
}
